import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { Plus, ShieldAlert } from "lucide-react";

import { LocationsList } from "~/components/locations/locations-list";
import { PageWrapper } from "~/components/page-wrapper";
import { TableSkeleton } from "~/components/skeletons/table-skeleton";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";

export const Route = createFileRoute("/_authenticated/locations/")({
  component: LocationsPage,
  pendingComponent: () => (
    <TableSkeleton
      description="View and manage all locations below"
      title="Your Locations"
    />
  ),
});

function LocationsPage() {
  const navigate = useNavigate();
  const { hasPermission, isLoading, hasEmployee } = useEmployeePermissions();
  const canViewIntegrations = hasPermission("viewIntegrations");

  // Loading state
  if (isLoading) {
    return (
      <PageWrapper
        description="View and manage all locations below"
        title="Your Locations"
      >
        <Skeleton className="h-96 w-full" />
      </PageWrapper>
    );
  }

  // No employee record linked
  if (!hasEmployee) {
    return (
      <PageWrapper
        description="View and manage all locations below"
        title="Your Locations"
      >
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>No Employee Profile</AlertTitle>
          <AlertDescription>
            Your account is not linked to an employee profile. Contact your
            organization admin to set up your employee record with the
            appropriate permissions.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  // No permission to view locations
  if (!canViewIntegrations) {
    return (
      <PageWrapper
        description="View and manage all locations below"
        title="Your Locations"
      >
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to view locations and integrations.
            Contact your manager to request access.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  return (
    <>
      <PageWrapper
        description="View and manage all locations below"
        title="Your Locations"
      >
        <div className="mb-4 flex justify-end">
          {/* V4.1: Route-based create for URL context tracking */}
          <Button
            onClick={() => navigate({ to: "/locations/new" })}
            size="lg"
            type="button"
          >
            <Plus className="size-4" />
            Add Location
          </Button>
        </div>
        <LocationsList />
      </PageWrapper>
      {/* Outlet for /locations/new modal */}
      <Outlet />
    </>
  );
}
